import { useState, useEffect, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { loadFromSupabase, saveToSupabase, getCached } from "../supabaseDB";
import { uploadDataUrlToStorage } from "../storageImages";

/* GFI Company Profile — single-record settings UI.
 *
 * Source of truth: Supabase `company_profile` (singleton row, PK='gfi').
 * Also kept mirrored in localStorage under "gfi-company-profile" so the
 * sync `loadCompanyProfile()` helper exported below keeps working for
 * document builders (Bill of Lading, Sales Report, etc.) until those
 * are migrated to async loads in a later paso.
 *
 * Defaults below match the values Arturo confirmed on 2026-05-15. */

const STORAGE_KEY = "gfi-company-profile";

export const DEFAULT_COMPANY = {
  legalName: "Ground-Fresh International Inc.",
  dba: "",
  taxId: "47-2438437",
  addressLine: "152 W Hoffman Ave Ste 5",
  city: "Lindenhurst",
  state: "NY",
  zip: "11757",
  country: "USA",
  phone: "",
  email: "",
  website: "ground-fresh.com",
  organicCert: "PA-OR-1186",
  driveRootPath: "ground fresh/incoming shipments",
  localDrivePath: "G:\\My Drive\\ground fresh\\incoming shipments",
  outboundEmail: "a.gonzalez@ground-fresh.com",
  emailAccounts: [],
  emailSignatures: [],
  emailTemplates: {
    /* Per-doc-type defaults. The field flags decide which fact rows
       get auto-appended at the bottom of the body when an email is
       composed for that doc kind. */
    receiving: {
      body: "Good morning,\n\nPlease find attached the loading documents for the upcoming inbound shipment.\n\nThanks,\nGround Fresh International",
      fields: { pu: false, po: false, lot: true, arrival: true, shipper: true, client: false, warehouse: true },
    },
    inbound: {
      body: "Hi,\n\nPlease find attached the documents for the upcoming delivery.\n\nThanks,\nGround Fresh International",
      fields: { pu: true, po: true, lot: true, arrival: true, shipper: false, client: true, warehouse: true },
    },
    pickTicket: {
      body: "Hi,\n\nPlease find attached the pick ticket for the upcoming pickup.\n\nThanks,\nGround Fresh International",
      fields: { pu: true, po: true, lot: true, arrival: true, shipper: false, client: true, warehouse: true },
    },
    invoice: {
      body: "Hi,\n\nPlease find attached the invoice for your recent order.\n\nThanks,\nGround Fresh International",
      fields: { pu: true, po: true, lot: true, arrival: false, shipper: false, client: true, warehouse: false },
    },
    salesReport: {
      body: "Hello,\n\nPlease find attached the sales report.\n\nThanks,\nGround Fresh International",
      fields: { pu: false, po: false, lot: true, arrival: false, shipper: true, client: false, warehouse: false },
    },
  },
  notes: "",
  /* Appended to the end of every email sent from the GF account in
     Ground-Fresh flows (InvoiceRegistry, ShipmentRegistry, etc.) via
     emailHelpers.js's appendDisclosureByAccount. SweetLeaf's own
     disclosure is a separate setting (sweetleaf_settings.
     confidentialityDisclosure, edited in SweetLeaf Settings) — the two
     are intentionally not shared, see emailHelpers.js's header comment. */
  confidentialityDisclosureGf: "CONFIDENTIALITY NOTICE\nThe information contained in this e-mail is confidential and/or proprietary to Ground-fresh International Inc. and/or its affiliates and may only be used solely in performance of work or services for Ground-Fresh International Inc. The information transmitted herewith is intended only for use by the individual or entity to which it is addressed. If the reader of this message is not the intended recipient, you are hereby notified that any review, retransmission, dissemination, distribution, copying or other use of, or taking of any action in reliance upon this information is strictly prohibited. If you have received this communication in error, please contact the sender and delete the material from your computer.",
  /* Read by paymentConfirmation.js's buildPaymentConfirmationHTML —
     ALWAYS printed at the foot of every Payment/Deposit Confirmation
     PDF, any module. One field per language; the PDF picks the one
     matching the active UI lang at generation time. */
  marketConditionsNoticeEn: "MARKET CONDITIONS NOTICE: Payments and returns shown in this confirmation are subject to prevailing market conditions. Sudden market fluctuations, demand changes, or unforeseen events may affect final settlement amounts. Ground Fresh International Inc. does not guarantee that current amounts will match final settlements in cases of significant market volatility.",
  marketConditionsNoticeEs: "AVISO DE CONDICIONES DE MERCADO: Los pagos y retornos mostrados en esta confirmación están sujetos a las condiciones de mercado vigentes. Fluctuaciones repentinas del mercado, cambios en la demanda, o eventos imprevistos pueden afectar los montos finales de liquidación. Ground Fresh International Inc. no garantiza que los montos actuales coincidan con las liquidaciones finales en casos de volatilidad significativa del mercado.",
  /* Same PDF, but only printed when the batch includes at least one
     row with a non-goodDelivery saleStatus (Protection, PAS, etc.). */
  protectionNoticeEn: "PROTECTION NOTICE: Lots or pallets marked as 'Protection' or 'PAS' status are subject to additional price adjustment. The original sale price is not guaranteed as the final return — actual settlements may reflect deductions based on quality issues or agreed terms.",
  protectionNoticeEs: "AVISO DE PROTECCIÓN: Los lotes o pallets marcados con estatus 'Protección' o 'PAS' están sujetos a ajustes de precio adicionales. El precio de venta original no se garantiza como retorno final — las liquidaciones reales pueden reflejar deducciones basadas en problemas de calidad o términos acordados.",
  /* Quality Report (Texas QC) disclosure — 2026-09-14, Arturo asked
     for a more professional rewrite of: "Este reporte representa
     condicion de llegada de la fruta a Texas, no necesariamente
     implica que la fruta haya sido aceptada o rechazada, el cliente
     final reportara a su llegada la condicion definitiva". Printed in
     the "Send Quality Report" shipper email (ShipmentRegistry.js) and
     on its auto-attached PDF (qualityReport.js). */
  qualityReportDisclosureEn: "This report reflects the condition of the fruit upon arrival at our Texas facility and is provided for informational purposes only. It should not be interpreted as a formal acceptance or rejection of the merchandise — the end client will report the definitive condition upon their own receipt of the load.",
  qualityReportDisclosureEs: "Este reporte refleja la condición de la fruta a su llegada a nuestras instalaciones en Texas y tiene fines exclusivamente informativos. No debe interpretarse como una aceptación o rechazo formal de la mercancía — el cliente final reportará la condición definitiva al momento de su propia recepción del embarque.",
  /* 2026-10-01 — Arturo: the disclosure above doesn't fit a ClariFresh
     (Twin River) report — that inspection already IS the end client's
     own receiving check, the fruit doesn't travel on to another
     client for a separate inspection. Separate wording, used only
     when shipment.texasQcMode === "clarifresh" (qualityReport.js).
     Always Spanish, same as the report itself. */
  qualityReportDisclosureClarifreshEs: "Este reporte refleja la condición de la fruta a su llegada a Texas y tiene fines exclusivamente informativos. No debe interpretarse como una aceptación o rechazo formal de la mercancía. De considerarse necesario, se tomarán las acciones correctivas correspondientes para minimizar el impacto en la entrega a tienda.",
};

/* Field labels for the email-template field checkboxes. Shared by
   the UI and the runtime composer (composeEmailBody). Order matters
   — that's the order rows appear in the rendered body. */
export const TEMPLATE_FIELD_ORDER = ["pu", "po", "lot", "arrival", "shipper", "client", "warehouse"];
export const TEMPLATE_FIELD_LABELS = {
  pu: "PU#",
  po: "PO#",
  lot: "LOT",
  arrival: "ETA",
  shipper: "Shipper",
  client: "Client",
  warehouse: "Warehouse",
};

/* Build the final email body text from a template + a values map.
   Falls back to just the body text when no fields are flagged.
   `opts.labels` lets the caller override the label of any field (e.g.
   the PU# row shows "Inbound" when the user picked the Inbound
   variant in the Invoice email dialog, "PU#" when Pick Ticket). */
export function composeEmailBody(template, values, opts) {
  if (!template) return "";
  const base = String(template.body || "").trim();
  const fields = template.fields || {};
  const labelOverrides = (opts && opts.labels) || {};
  const rows = [];
  TEMPLATE_FIELD_ORDER.forEach(function (k) {
    if (fields[k] && values && values[k] != null && String(values[k]).trim() !== "") {
      const label = labelOverrides[k] || TEMPLATE_FIELD_LABELS[k];
      rows.push(label + ": " + values[k]);
    }
  });
  if (rows.length === 0) return base;
  /* Inject the fact rows just before the closing "Thanks, …" line
     when present, otherwise append at the end. Keeping the rows
     above the signoff reads more naturally. */
  const m = base.match(/\n\nThanks,[\s\S]*$/i) || base.match(/\n\nSaludos[\s\S]*$/i);
  if (m) {
    const idx = m.index;
    return base.substring(0, idx) + "\n\n" + rows.join("\n") + base.substring(idx);
  }
  return base + "\n\n" + rows.join("\n");
}

/* Resolve which template to use for a given send context. */
export function pickEmailTemplate(profile, docKind) {
  const all = (profile && profile.emailTemplates) || DEFAULT_COMPANY.emailTemplates;
  return all[docKind] || all.inbound || { body: "", fields: {} };
}

/* Read with default fallback. Used by document builders that need
   the canonical company block — falls back to DEFAULT_COMPANY when
   the key isn't yet seeded so the docs never render with blanks. */
export function loadCompanyProfile() {
  /* Source of truth is Supabase. Read from its in-memory cache
     (synchronous, primed by primeCatalogs at startup) so all
     document/email builders get the real profile — including
     emailAccounts with maskedFrom. Fall back to the localStorage
     mirror only if the cache isn't primed yet. */
  try {
    var cached = getCached("company_profile");
    if (cached && cached.length > 0) {
      var rec = cached.find(function (r) { return String(r.id) === "gfi"; }) || cached[0];
      if (rec) return { ...DEFAULT_COMPANY, ...rec };
    }
  } catch (e) { /* fall through to localStorage */ }
  try {
    var raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_COMPANY };
    var parsed = JSON.parse(raw);
    return { ...DEFAULT_COMPANY, ...parsed };
  } catch (e) {
    return { ...DEFAULT_COMPANY };
  }
}

const T = {
  en: {
    title: "Company Profile",
    subtitle: "Used as the consignee / importer block on Carta Porte, Receiving Sheet and other printed documents.",
    legal: "Legal name", dba: "DBA / Trade name",
    taxId: "Tax ID (EIN)", taxIdSub: "U.S. Employer Identification Number",
    address: "Address",
    addressLine: "Street address", city: "City", state: "State", zip: "ZIP", country: "Country",
    phone: "Phone", email: "Email", website: "Website",
    organicCert: "USDA Organic Cert #",
    driveRootPath: "Google Drive root path",
    driveRootPathSub: "Folder under My Drive where new embarque + invoice folders are created. Path segments are separated by '/'. Default: ground fresh/incoming shipments",
    localDrivePath: "Local Drive base path (Windows)",
    localDrivePathSub: "Local mount of the Drive root used by the Open folder buttons. Default: G:\\My Drive\\ground fresh\\incoming shipments",
    outboundEmail: "Outbound email account",
    outboundEmailSub: "Google account that sends emails from the app. Must be the same Google account used to sign in to Drive. Default: a.gonzalez@ground-fresh.com",
    emailAccountsLabel: "Email Accounts",
    emailAccountsSub: "Multiple sending accounts. Each has a name, Gmail address, and its own HTML signature. The first account is used by default.",
    emailAccountName: "Account name",
    emailAccountEmail: "Gmail address",
    emailAccountAdd: "+ Add account",
    emailAccountRemove: "Remove",
    emailAccountSigLabel: "Signature HTML",
    emailAccountSigUpload: "Upload .html",
    emailAccountSigUploadErr: "Could not read file",
    emailAccountSigPreview: "Preview",
    disclosureGfSection: "Ground Fresh Email Confidentiality Notice",
    disclosureGfSectionSub: "This text is added automatically at the end of emails sent from the GF account in Ground Fresh flows. SweetLeaf's disclosure is managed separately in SweetLeaf Settings.",
    marketNoticeSection: "Market Conditions Notice",
    marketNoticeSectionSub: "Legal notice added to the bottom of every Payment Confirmation PDF. Edit here if the disclaimer needs to change.",
    protectionNoticeSection: "Protection Notice",
    protectionNoticeSectionSub: "Legal notice added only when the confirmation includes lots or pallets with 'Protection' or 'PAS' status.",
    qualityReportDisclosureSection: "Quality Report (Texas QC) Disclosure",
    qualityReportDisclosureSectionSub: "Added to the \"Send Quality Report\" shipper email and its attached PDF.",
    qualityReportDisclosureClarifreshSection: "Quality Report Disclosure — ClariFresh",
    qualityReportDisclosureClarifreshSectionSub: "Used instead of the one above when the report is ClariFresh's (3-column, Twin River). Spanish only — the report is always Spanish.",
    sigSection: "Email signatures",
    sigSectionSub: "Saved HTML signatures appended to outbound emails. The default one is added automatically; the email composer lets you switch before sending.",
    sigName: "Signature name",
    sigHtml: "HTML",
    sigHtmlHint: "Paste the rendered HTML of the signature (image tags, links, etc.) — or upload an .html file.",
    sigUpload: "Upload .html",
    sigUploadErr: "Could not read the file",
    sigPreview: "Preview",
    sigDefault: "Default",
    sigSetDefault: "Set as default",
    sigAdd: "+ New signature",
    sigRemove: "Remove",
    sigEmpty: "No signatures saved yet",
    tplSection: "Email templates",
    tplSectionSub: "Per-document-kind email bodies. When composing an email, the matching template populates the message + auto-appends the checked fields from the record.",
    tplReceiving: "Receiving Sheet (Shipments)",
    tplInbound: "Inbound / Pick Ticket (Invoices)",
    tplPickTicket: "Pick Ticket",
    tplInvoice: "Invoice (Invoices)",
    tplSalesReport: "Sales Report (Bills)",
    tplBody: "Body",
    tplFields: "Auto-append fields",
    tplPreview: "Preview",
    tplFieldPu: "PU#",
    tplFieldPo: "PO#",
    tplFieldLot: "LOT",
    tplFieldArrival: "Arrival (ETA)",
    tplFieldShipper: "Shipper",
    tplFieldClient: "Client",
    tplFieldWarehouse: "Warehouse",
    notes: "Internal notes",
    save: "Save", reset: "Reset to defaults", saved: "Profile saved", restoredDefaults: "Defaults restored",
    confirmReset: "Reset all fields to GFI defaults? This overwrites the current values.",
  },
  es: {
    title: "Perfil de la Empresa",
    subtitle: "Se usa como bloque de consignatario / importador en Carta Porte, Receiving Sheet y otros documentos impresos.",
    legal: "Razón social", dba: "DBA / Nombre comercial",
    taxId: "Tax ID (EIN)", taxIdSub: "Número de identificación fiscal de EE.UU.",
    address: "Domicilio",
    addressLine: "Calle y número", city: "Ciudad", state: "Estado", zip: "Código Postal", country: "País",
    phone: "Teléfono", email: "Email", website: "Sitio web",
    organicCert: "USDA Cert. Orgánico #",
    driveRootPath: "Ruta raíz en Google Drive",
    driveRootPathSub: "Carpeta dentro de Mi unidad donde se crean las carpetas de embarques + facturas. Separa niveles con '/'. Predeterminado: ground fresh/incoming shipments",
    localDrivePath: "Ruta local del Drive (Windows)",
    localDrivePathSub: "Carpeta local montada por el Drive Desktop, usada por los botones Abrir Carpeta. Predeterminado: G:\\My Drive\\ground fresh\\incoming shipments",
    outboundEmail: "Cuenta de email de salida",
    outboundEmailSub: "Cuenta de Google desde la que se envían los correos de la app. Debe ser la misma con la que inicias sesión en Drive. Predeterminado: a.gonzalez@ground-fresh.com",
    emailAccountsLabel: "Cuentas de Email",
    emailAccountsSub: "Múltiples cuentas de envío. Cada una tiene nombre, dirección de Gmail y su propia firma HTML. La primera cuenta se usa por defecto.",
    emailAccountName: "Nombre de cuenta",
    emailAccountEmail: "Dirección de Gmail",
    emailAccountAdd: "+ Agregar cuenta",
    emailAccountRemove: "Eliminar",
    emailAccountSigLabel: "Firma HTML",
    emailAccountSigUpload: "Subir .html",
    emailAccountSigUploadErr: "No se pudo leer el archivo",
    emailAccountSigPreview: "Vista previa",
    disclosureGfSection: "Aviso de Confidencialidad Ground Fresh",
    disclosureGfSectionSub: "Este texto se agrega automáticamente al final de los emails enviados desde la cuenta GF en flows de Ground Fresh. El disclosure de SweetLeaf se gestiona por separado en SweetLeaf Settings.",
    marketNoticeSection: "Aviso de Condiciones de Mercado",
    marketNoticeSectionSub: "Aviso legal agregado al pie de cada PDF de Confirmación de Pago. Edita aquí si el disclaimer necesita cambiar.",
    protectionNoticeSection: "Aviso de Protección",
    protectionNoticeSectionSub: "Aviso legal agregado solo cuando la confirmación incluye lotes o pallets con estatus 'Protección' o 'PAS'.",
    qualityReportDisclosureSection: "Aviso del Reporte de Calidad (Texas QC)",
    qualityReportDisclosureSectionSub: "Se agrega al email de \"Enviar Reporte de Calidad\" al shipper y a su PDF anexo.",
    qualityReportDisclosureClarifreshSection: "Aviso del Reporte de Calidad — ClariFresh",
    qualityReportDisclosureClarifreshSectionSub: "Se usa en vez del anterior cuando el reporte es de ClariFresh (3 columnas, Twin River). Solo en español — el reporte siempre es en español.",
    sigSection: "Firmas de email",
    sigSectionSub: "Firmas HTML guardadas que se agregan al final de los correos. La marcada como predeterminada se incluye automáticamente; al redactar puedes cambiarla antes de enviar.",
    sigName: "Nombre de la firma",
    sigHtml: "HTML",
    sigHtmlHint: "Pega el HTML renderizado de la firma (etiquetas img, links, etc.) — o sube un archivo .html.",
    sigUpload: "Subir .html",
    sigUploadErr: "No se pudo leer el archivo",
    sigPreview: "Vista previa",
    sigDefault: "Predeterminada",
    sigSetDefault: "Marcar como predeterminada",
    sigAdd: "+ Nueva firma",
    sigRemove: "Eliminar",
    sigEmpty: "Aún no hay firmas guardadas",
    tplSection: "Plantillas de email",
    tplSectionSub: "Cuerpos de correo por tipo de documento. Al redactar un correo, la plantilla correspondiente carga el mensaje + agrega automáticamente los campos marcados.",
    tplReceiving: "Receiving Sheet (Embarques)",
    tplInbound: "Inbound / Pick Ticket (Facturas)",
    tplPickTicket: "Pick Ticket",
    tplInvoice: "Factura (Facturas)",
    tplSalesReport: "Sales Report (Bills)",
    tplBody: "Cuerpo",
    tplFields: "Agregar campos",
    tplPreview: "Vista previa",
    tplFieldPu: "PU#",
    tplFieldPo: "PO#",
    tplFieldLot: "LOT",
    tplFieldArrival: "Llegada (ETA)",
    tplFieldShipper: "Shipper",
    tplFieldClient: "Cliente",
    tplFieldWarehouse: "Bodega",
    notes: "Notas internas",
    save: "Guardar", reset: "Restaurar valores", saved: "Perfil guardado", restoredDefaults: "Valores restaurados",
    confirmReset: "¿Restaurar todos los campos a los valores por defecto de GFI? Se sobrescriben los actuales.",
  },
};

const inp = { padding: "8px 10px", fontSize: 13, border: "1px solid var(--color-border)", borderRadius: 6, background: "var(--color-bg-input)", color: "var(--color-text)", outline: "none", fontFamily: "inherit", width: "100%", boxSizing: "border-box" };

function Field({ label, sub, children, span }) {
  return (
    <div style={{ gridColumn: span ? "span " + span : "auto" }}>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.04em" }}>{label}</label>
      {children}
      {sub && <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{sub}</div>}
    </div>
  );
}

function Toast({ message, onClose }) {
  var [fading, setFading] = useState(false);
  useEffect(() => {
    var fadeT = setTimeout(() => setFading(true), 2700);
    var closeT = setTimeout(() => { onClose(); }, 3000);
    return () => { clearTimeout(fadeT); clearTimeout(closeT); };
  }, [message]);
  return createPortal(<div style={{ position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: "#1a2636", color: "#fff", padding: "10px 22px", borderRadius: 10, fontSize: 13, fontWeight: 600, zIndex: 999999, boxShadow: "0 8px 32px rgba(0,0,0,0.2)", animation: fading ? "fadeOut 0.3s ease forwards" : undefined }}>{message}</div>, document.body);
}

export default function CompanyProfile({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang];
  var [form, setForm] = useState(() => loadCompanyProfile());
  var [dirty, setDirty] = useState(false);
  var [toast, setToast] = useState(null);
  var [error, setError] = useState(null);
  var [saving, setSaving] = useState(false);
  var [loading, setLoading] = useState(true);
  var [logoBusy, setLogoBusy] = useState(false);
  var logoInputRef = useRef(null);
  var aliveRef = useRef(true);

  /* Mount: pull the singleton from Supabase. If none exists yet (fresh
     install), fall back to whatever loadCompanyProfile() returned from
     the local cache, which itself falls back to DEFAULT_COMPANY. */
  useEffect(() => {
    aliveRef.current = true;
    setError(null); setLoading(true);
    loadFromSupabase("company_profile")
      .then(rows => {
        if (!aliveRef.current) return;
        if (rows && rows.length > 0) {
          var rec = rows[0];
          /* Merge over DEFAULT_COMPANY so any field the schema doesn't
             carry stays at its default (e.g. legacy "dba" if not set). */
          setForm({ ...DEFAULT_COMPANY, ...rec });
        }
      })
      .catch(e => { if (aliveRef.current) setError("Could not load profile: " + (e.message || String(e))); })
      .finally(() => { if (aliveRef.current) setLoading(false); });
    return () => { aliveRef.current = false; };
  }, []);

  var set = useCallback((k, v) => { setForm(prev => ({ ...prev, [k]: v })); setDirty(true); }, []);
  var handleLogoFile = async function (e) {
    var file = e.target.files && e.target.files[0];
    if (e.target) e.target.value = "";
    if (!file) return;
    if (!/^image\//.test(file.type || "")) { setError(lang === "es" ? "El logo debe ser una imagen." : "Logo must be an image."); return; }
    setLogoBusy(true);
    try {
      var dataUrl = await new Promise(function (resolve, reject) {
        var r = new FileReader();
        r.onload = function (ev) { resolve(ev.target.result); };
        r.onerror = function () { reject(new Error("read failed")); };
        r.readAsDataURL(file);
      });
      var up = await uploadDataUrlToStorage("product-labels", "gfi-company-logo", dataUrl);
      set("logoUrl", up.publicUrl);
      set("logoPath", up.path);
      setToast(lang === "es" ? "Logo subido. Guarda para aplicar." : "Logo uploaded. Save to apply.");
    } catch (err) {
      setError((lang === "es" ? "Error al subir el logo: " : "Logo upload failed: ") + ((err && err.message) || String(err)));
    }
    setLogoBusy(false);
  };

  /* Defensive string coercion — Supabase returns null for empty text
     columns. Used in defaults merge so .trim() / .toUpperCase() in
     downstream consumers don't crash on nulls. */
  var S = function (v) { return v == null ? "" : String(v); };

  var handleSave = async () => {
    if (saving) return;
    setSaving(true); setError(null);
    try {
      /* Pass the form as-is; supabaseDB applies the singletonId='gfi'
         and the legalName↔company_name field mapping from TABLE_CONFIG. */
      var saved = await saveToSupabase("company_profile", form);
      /* Dual write: keep localStorage mirror in sync so the exported
         loadCompanyProfile() sync helper stays accurate for legacy
         consumers (Bill of Lading, Sales Report). */
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(form)); } catch (e) {}
      setForm({ ...DEFAULT_COMPANY, ...saved, /* preserve any null→"" defaults */ legalName: S(saved.legalName) || form.legalName });
      setDirty(false);
      setToast(t.saved);
    } catch (e) {
      setError("Could not save profile: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };
  var handleReset = async () => {
    if (!window.confirm(t.confirmReset)) return;
    if (saving) return;
    setSaving(true); setError(null);
    try {
      var saved = await saveToSupabase("company_profile", { ...DEFAULT_COMPANY });
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(DEFAULT_COMPANY)); } catch (e) {}
      setForm({ ...DEFAULT_COMPANY, ...saved });
      setDirty(false);
      setToast(t.restoredDefaults);
    } catch (e) {
      setError("Could not reset: " + (e.message || String(e)));
      setToast("⚠ " + e.message);
    } finally { setSaving(false); }
  };

  return (
    /* maxHeight + overflowY makes the profile a self-scrolling pane
       so the Save bar, Notes, and Email Signatures stay reachable
       on tall forms even when the surrounding shell doesn't grow
       (some flex layouts cap the module's height). 140px ≈ top-nav
       (64) + greeting strip (~50) + outer padding. */
    <div style={{ maxWidth: 880, maxHeight: "calc(100vh - 140px)", overflowY: "auto", paddingRight: 4 }}>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, margin: 0 }}>{t.title}</h2>
        <p style={{ fontSize: 12, color: "var(--color-text-muted)", marginTop: 4, lineHeight: 1.5 }}>{t.subtitle}</p>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
        <div onClick={function () { if (!logoBusy && logoInputRef.current) logoInputRef.current.click(); }} title={lang === "es" ? "Cambiar logo" : "Change logo"} style={{ width: 64, height: 64, borderRadius: 10, border: "1px dashed var(--color-border)", background: "var(--color-bg)", cursor: logoBusy ? "wait" : "pointer", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", flexShrink: 0 }}>
          {form.logoUrl
            ? <img src={form.logoUrl} alt="logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
            : <span style={{ fontSize: 10, color: "var(--color-text-muted)", textAlign: "center", padding: 4 }}>{logoBusy ? "…" : (lang === "es" ? "+ Logo" : "+ Logo")}</span>}
        </div>
        <input ref={logoInputRef} type="file" accept="image/*" onChange={handleLogoFile} style={{ display: "none" }} />
        <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>{lang === "es" ? "Logo de la empresa (aparece en el encabezado de tu portal)." : "Company logo (shown in your portal header)."}</div>
      </div>
      {error && <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.3)", marginBottom: 12, fontSize: 12, color: "#B91C1C", fontWeight: 500 }}><span style={{ fontSize: 14 }}>⚠</span><span style={{ flex: 1, fontFamily: "'JetBrains Mono', monospace", wordBreak: "break-word" }}>{error}</span></div>}
      {loading && !dirty && <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 8, fontStyle: "italic" }}>{lang === "es" ? "Cargando perfil…" : "Loading profile…"}</div>}

      <div style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 20 }}>
        {/* Identity block */}
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12, marginBottom: 16 }}>
          <Field label={t.legal}><input value={form.legalName || ""} onChange={e => set("legalName", e.target.value)} style={inp} /></Field>
          <Field label={t.dba}><input value={form.dba || ""} onChange={e => set("dba", e.target.value)} style={inp} placeholder="—" /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 16 }}>
          <Field label={t.taxId} sub={t.taxIdSub}><input value={form.taxId || ""} onChange={e => set("taxId", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600, letterSpacing: "0.05em" }} /></Field>
          <Field label={t.organicCert}><input value={form.organicCert || ""} onChange={e => set("organicCert", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600 }} /></Field>
        </div>

        {/* Address */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.address}</div>
        <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Field label={t.addressLine} span={3}><input value={form.addressLine || ""} onChange={e => set("addressLine", e.target.value)} style={inp} /></Field>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr 1fr", gap: 12, marginBottom: 18 }}>
          <Field label={t.city}><input value={form.city || ""} onChange={e => set("city", e.target.value)} style={inp} /></Field>
          <Field label={t.state}><input value={form.state || ""} onChange={e => set("state", e.target.value)} style={inp} maxLength={3} /></Field>
          <Field label={t.zip}><input value={form.zip || ""} onChange={e => set("zip", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} /></Field>
          <Field label={t.country}><input value={form.country || ""} onChange={e => set("country", e.target.value)} style={inp} /></Field>
        </div>

        {/* Contact */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{lang === "es" ? "Contacto" : "Contact"}</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 18 }}>
          <Field label={t.phone}><input value={form.phone || ""} onChange={e => set("phone", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="—" /></Field>
          <Field label={t.email}><input type="email" value={form.email || ""} onChange={e => set("email", e.target.value)} style={inp} placeholder="—" /></Field>
          <Field label={t.website}><input value={form.website || ""} onChange={e => set("website", e.target.value)} style={inp} /></Field>
        </div>

        {/* Google Drive integration */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>Google Drive & Gmail</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
          <Field label={t.driveRootPath} sub={t.driveRootPathSub}><input value={form.driveRootPath || ""} onChange={e => set("driveRootPath", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="ground fresh/incoming shipments" /></Field>
          <Field label={t.localDrivePath} sub={t.localDrivePathSub}><input value={form.localDrivePath || ""} onChange={e => set("localDrivePath", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="G:\\My Drive\\ground fresh\\incoming shipments" /></Field>
        </div>
        <div style={{ marginBottom: 18 }}>
          <Field label={t.outboundEmail} sub={t.outboundEmailSub}><input type="email" value={form.outboundEmail || ""} onChange={e => set("outboundEmail", e.target.value)} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} placeholder="a.gonzalez@ground-fresh.com" /></Field>
        </div>

        {/* Email Accounts — multiple sending identities with per-account signature */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 2 }}>{t.emailAccountsLabel}</div>
          <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 10, fontStyle: "italic" }}>{t.emailAccountsSub}</div>
          {(form.emailAccounts || []).map((acct, idx) => (
            <div key={acct.id || idx} style={{ padding: 12, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", marginBottom: 10 }}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8 }}>
                <input type="text" placeholder={t.emailAccountName} value={acct.name || ""} onChange={e => set("emailAccounts", (form.emailAccounts || []).map((a, i) => i === idx ? { ...a, name: e.target.value } : a))} style={{ ...inp, fontWeight: 600 }} />
                <input type="email" placeholder={t.emailAccountEmail} value={acct.email || ""} onChange={e => set("emailAccounts", (form.emailAccounts || []).map((a, i) => i === idx ? { ...a, email: e.target.value } : a))} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
                <button type="button" onClick={() => set("emailAccounts", (form.emailAccounts || []).filter((_, i) => i !== idx))} style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid rgba(231,76,60,0.3)", background: "rgba(231,76,60,0.06)", color: "var(--color-danger)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>× {t.emailAccountRemove}</button>
              </div>
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 4 }}>{lang === "es" ? "Alias enmascarado (gfinexus.com)" : "Masked alias (gfinexus.com)"}</div>
                <input type="email" placeholder="usuario.cuenta@gfinexus.com" value={acct.maskedFrom || ""} onChange={e => set("emailAccounts", (form.emailAccounts || []).map((a, i) => i === idx ? { ...a, maskedFrom: e.target.value } : a))} style={{ ...inp, fontFamily: "'JetBrains Mono', monospace" }} />
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 2, fontStyle: "italic" }}>{lang === "es" ? "Los emails salen desde este alias; las respuestas llegan al email principal de arriba." : "Emails are sent from this alias; replies go to the main email above."}</div>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4, marginTop: 12 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.emailAccountSigLabel}</label>
                <label style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 5, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                  ⬆ {t.emailAccountSigUpload}
                  <input type="file" accept=".html,.htm,text/html" style={{ display: "none" }} onChange={(e) => { const f2 = e.target.files && e.target.files[0]; if (!f2) return; const r = new FileReader(); r.onload = (ev) => { set("emailAccounts", (form.emailAccounts || []).map((a, i) => i === idx ? { ...a, signature: String(ev.target.result || "") } : a)); }; r.onerror = () => alert(t.emailAccountSigUploadErr); r.readAsText(f2); e.target.value = ""; }} />
                </label>
              </div>
              <textarea value={acct.signature || ""} onChange={e => set("emailAccounts", (form.emailAccounts || []).map((a, i) => i === idx ? { ...a, signature: e.target.value } : a))} rows={4} style={{ ...inp, resize: "vertical", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }} placeholder="<div>...</div>" />
              {acct.signature ? (
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{t.emailAccountSigPreview}</div>
                  <div style={{ padding: 10, borderRadius: 6, border: "1px dashed var(--color-border)", background: "#ffffff", color: "#1a1a1a" }} dangerouslySetInnerHTML={{ __html: acct.signature }} />
                </div>
              ) : null}
            </div>
          ))}
          <button type="button" onClick={() => { const list = form.emailAccounts || []; set("emailAccounts", list.concat([{ id: Date.now() + Math.random(), name: "", email: "", signature: "" }])); }} style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600, width: "100%" }}>{t.emailAccountAdd}</button>
        </div>

        {/* Email signatures — multiple named HTML blobs, one marked
            default. EmailDialog reads this list and appends the
            selected signature below the body before sending. */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.sigSection}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10, fontStyle: "italic" }}>{t.sigSectionSub}</div>
        {(form.emailSignatures || []).length === 0 && (
          <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontStyle: "italic", marginBottom: 10 }}>{t.sigEmpty}</div>
        )}
        {(form.emailSignatures || []).map((sig, idx) => (
          <div key={sig.id || idx} style={{ padding: 12, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", marginBottom: 10 }}>
            <div style={{ display: "grid", gridTemplateColumns: "1fr auto auto", gap: 8, marginBottom: 8 }}>
              <input
                type="text"
                placeholder={t.sigName}
                value={sig.name || ""}
                onChange={e => set("emailSignatures", (form.emailSignatures || []).map((s, i) => i === idx ? { ...s, name: e.target.value } : s))}
                style={{ ...inp, fontWeight: 600 }}
              />
              <button
                type="button"
                onClick={() => set("emailSignatures", (form.emailSignatures || []).map((s, i) => ({ ...s, isDefault: i === idx })))}
                style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid " + (sig.isDefault ? "var(--color-accent)" : "var(--color-border)"), background: sig.isDefault ? "var(--color-accent-bg, rgba(42,122,111,0.08))" : "transparent", color: sig.isDefault ? "var(--color-accent)" : "var(--color-text-secondary)", cursor: "pointer", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" }}
              >
                {sig.isDefault ? "★ " + t.sigDefault : t.sigSetDefault}
              </button>
              <button
                type="button"
                onClick={() => set("emailSignatures", (form.emailSignatures || []).filter((_, i) => i !== idx))}
                style={{ padding: "7px 12px", borderRadius: 6, border: "1px solid rgba(231,76,60,0.3)", background: "rgba(231,76,60,0.06)", color: "var(--color-danger)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}
              >× {t.sigRemove}</button>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.sigHtml}</label>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 5, padding: "5px 10px", borderRadius: 5, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 11, fontWeight: 600 }}>
                ⬆ {t.sigUpload}
                <input
                  type="file"
                  accept=".html,.htm,text/html"
                  style={{ display: "none" }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (!f) return;
                    const r = new FileReader();
                    r.onload = (ev) => {
                      const html = String(ev.target.result || "");
                      set("emailSignatures", (form.emailSignatures || []).map((s, i) => i === idx ? { ...s, html: html, name: s.name || f.name.replace(/\.html?$/i, "") } : s));
                    };
                    r.onerror = () => alert(t.sigUploadErr);
                    r.readAsText(f);
                    e.target.value = "";
                  }}
                />
              </label>
            </div>
            <textarea
              value={sig.html || ""}
              onChange={e => set("emailSignatures", (form.emailSignatures || []).map((s, i) => i === idx ? { ...s, html: e.target.value } : s))}
              rows={6}
              style={{ ...inp, resize: "vertical", fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
              placeholder='<div>...</div>'
            />
            <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginTop: 3, fontStyle: "italic" }}>{t.sigHtmlHint}</div>
            {sig.html ? (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 10, color: "var(--color-text-muted)", marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{t.sigPreview}</div>
                <div style={{ padding: 10, borderRadius: 6, border: "1px dashed var(--color-border)", background: "#ffffff", color: "#1a1a1a" }} dangerouslySetInnerHTML={{ __html: sig.html }} />
              </div>
            ) : null}
          </div>
        ))}
        <button
          type="button"
          onClick={() => {
            const list = form.emailSignatures || [];
            const isFirst = list.length === 0;
            set("emailSignatures", list.concat([{ id: Date.now() + Math.random(), name: "", html: "", isDefault: isFirst }]));
          }}
          style={{ padding: "8px 14px", borderRadius: 6, border: "1px dashed var(--color-border)", background: "transparent", color: "var(--color-accent)", cursor: "pointer", fontSize: 12, fontWeight: 600, marginBottom: 18, width: "100%" }}
        >{t.sigAdd}</button>

        {/* Ground Fresh disclosure — appended automatically to every
            email sent from the GF account by emailHelpers.js's
            appendDisclosureByAccount(). Free text, escaped before it
            lands in outgoing HTML (it's user-edited, unlike the
            signature above which is trusted HTML the user pasted
            deliberately). SweetLeaf's own disclosure is a completely
            separate setting — see SweetLeafSettingsPanel.js. */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.disclosureGfSection}</div>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10, fontStyle: "italic" }}>{t.disclosureGfSectionSub}</div>
          <textarea
            value={form.confidentialityDisclosureGf != null ? form.confidentialityDisclosureGf : ""}
            onChange={e => set("confidentialityDisclosureGf", e.target.value)}
            rows={10}
            style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }}
            placeholder={DEFAULT_COMPANY.confidentialityDisclosureGf}
          />
        </div>

        {/* Market Conditions Notice — read by paymentConfirmation.js,
            ALWAYS printed at the foot of every Payment/Deposit
            Confirmation PDF regardless of module or batch content. One
            field per language (marketConditionsNoticeEn/Es); the PDF
            picks whichever matches the active UI lang when generated. */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.marketNoticeSection}</div>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10, fontStyle: "italic" }}>{t.marketNoticeSectionSub}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>EN</div>
              <textarea value={form.marketConditionsNoticeEn != null ? form.marketConditionsNoticeEn : ""} onChange={e => set("marketConditionsNoticeEn", e.target.value)} rows={8} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} placeholder={DEFAULT_COMPANY.marketConditionsNoticeEn} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>ES</div>
              <textarea value={form.marketConditionsNoticeEs != null ? form.marketConditionsNoticeEs : ""} onChange={e => set("marketConditionsNoticeEs", e.target.value)} rows={8} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} placeholder={DEFAULT_COMPANY.marketConditionsNoticeEs} />
            </div>
          </div>
        </div>

        {/* Protection Notice — same PDF, but only printed when the
            batch includes at least one row with a non-goodDelivery
            saleStatus (see paymentConfirmation.js's nonGoodDeliveryStatuses). */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.protectionNoticeSection}</div>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10, fontStyle: "italic" }}>{t.protectionNoticeSectionSub}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>EN</div>
              <textarea value={form.protectionNoticeEn != null ? form.protectionNoticeEn : ""} onChange={e => set("protectionNoticeEn", e.target.value)} rows={8} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} placeholder={DEFAULT_COMPANY.protectionNoticeEn} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>ES</div>
              <textarea value={form.protectionNoticeEs != null ? form.protectionNoticeEs : ""} onChange={e => set("protectionNoticeEs", e.target.value)} rows={8} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} placeholder={DEFAULT_COMPANY.protectionNoticeEs} />
            </div>
          </div>
        </div>

        {/* Quality Report (Texas QC) disclosure — 2026-09-14, Arturo
            asked for a more professional rewrite of his own draft
            wording; printed in ShipmentRegistry.js's "Send Quality
            Report" email and its attached PDF (qualityReport.js). */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.qualityReportDisclosureSection}</div>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10, fontStyle: "italic" }}>{t.qualityReportDisclosureSectionSub}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>EN</div>
              <textarea value={form.qualityReportDisclosureEn != null ? form.qualityReportDisclosureEn : ""} onChange={e => set("qualityReportDisclosureEn", e.target.value)} rows={8} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} placeholder={DEFAULT_COMPANY.qualityReportDisclosureEn} />
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>ES</div>
              <textarea value={form.qualityReportDisclosureEs != null ? form.qualityReportDisclosureEs : ""} onChange={e => set("qualityReportDisclosureEs", e.target.value)} rows={8} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} placeholder={DEFAULT_COMPANY.qualityReportDisclosureEs} />
            </div>
          </div>
        </div>

        {/* 2026-10-01 — Arturo: the disclosure above assumes the fruit
            travels on to a separate end client for their own receiving
            inspection — true for GFI's own 2-column Texas QC, but not
            for a ClariFresh (3-column, Twin River) report, which
            already IS that final receiving check. Separate wording,
            used only when texasQcMode === "clarifresh". */}
        <div style={{ marginBottom: 18 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.qualityReportDisclosureClarifreshSection}</div>
          <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10, fontStyle: "italic" }}>{t.qualityReportDisclosureClarifreshSectionSub}</div>
          <textarea value={form.qualityReportDisclosureClarifreshEs != null ? form.qualityReportDisclosureClarifreshEs : ""} onChange={e => set("qualityReportDisclosureClarifreshEs", e.target.value)} rows={8} style={{ ...inp, resize: "vertical", fontSize: 12, lineHeight: 1.45 }} placeholder={DEFAULT_COMPANY.qualityReportDisclosureClarifreshEs} />
        </div>

        {/* Email templates — fixed list (no add/remove). Each entry
            has a body textarea + checkboxes for auto-appended fact
            rows. The runtime composer (composeEmailBody) reads this
            map by doc kind. */}
        <div style={{ fontSize: 11, fontWeight: 700, color: "var(--color-accent)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8, paddingBottom: 4, borderBottom: "1px solid var(--color-border)" }}>{t.tplSection}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginBottom: 10, fontStyle: "italic" }}>{t.tplSectionSub}</div>
        {(() => {
          const tplDefs = [
            { key: "receiving",   title: t.tplReceiving },
            { key: "inbound",     title: t.tplInbound },
            { key: "pickTicket",  title: t.tplPickTicket },
            { key: "invoice",     title: t.tplInvoice },
            { key: "salesReport", title: t.tplSalesReport },
          ];
          const fieldDefs = [
            { key: "pu",        label: t.tplFieldPu },
            { key: "po",        label: t.tplFieldPo },
            { key: "lot",       label: t.tplFieldLot },
            { key: "arrival",   label: t.tplFieldArrival },
            { key: "shipper",   label: t.tplFieldShipper },
            { key: "client",    label: t.tplFieldClient },
            { key: "warehouse", label: t.tplFieldWarehouse },
          ];
          const all = form.emailTemplates || {};
          const updateTpl = (key, patch) => set("emailTemplates", { ...all, [key]: { ...(all[key] || {}), ...patch } });
          const updateField = (tplKey, fieldKey, value) => {
            const cur = all[tplKey] || {};
            const fields = { ...(cur.fields || {}), [fieldKey]: value };
            updateTpl(tplKey, { fields });
          };
          /* Build the preview using sample values so the user can see
             which rows get auto-appended. */
          const previewValues = { pu: "12345", po: "PO-987", lot: "58026", arrival: "2026-05-20", shipper: "Gomez Berries", client: "Four Seasons", warehouse: "American Cold Storage" };
          return tplDefs.map(def => {
            const tpl = all[def.key] || { body: "", fields: {} };
            const preview = composeEmailBody(tpl, previewValues);
            return (
              <div key={def.key} style={{ padding: 12, borderRadius: 8, border: "1px solid var(--color-border)", background: "var(--color-bg)", marginBottom: 10 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: "var(--color-text)", marginBottom: 6 }}>{def.title}</div>
                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{t.tplBody}</div>
                    <textarea
                      value={tpl.body || ""}
                      onChange={e => updateTpl(def.key, { body: e.target.value })}
                      rows={5}
                      style={{ ...inp, resize: "vertical", fontSize: 12 }}
                    />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-secondary)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{t.tplFields}</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: 8, borderRadius: 6, border: "1px solid var(--color-border)", background: "var(--color-bg-card)" }}>
                      {fieldDefs.map(f => (
                        <label key={f.key} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, cursor: "pointer" }}>
                          <input type="checkbox" checked={!!(tpl.fields && tpl.fields[f.key])} onChange={e => updateField(def.key, f.key, e.target.checked)} />
                          {f.label}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
                <div style={{ marginTop: 8 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "var(--color-text-muted)", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{t.tplPreview}</div>
                  <pre style={{ padding: 10, borderRadius: 6, border: "1px dashed var(--color-border)", background: "#ffffff", color: "#1a1a1a", fontFamily: "inherit", fontSize: 11, lineHeight: 1.5, whiteSpace: "pre-wrap", margin: 0 }}>{preview}</pre>
                </div>
              </div>
            );
          });
        })()}

        {/* Notes */}
        <Field label={t.notes}><textarea value={form.notes || ""} onChange={e => set("notes", e.target.value)} rows={2} style={{ ...inp, resize: "vertical", fontSize: 12 }} /></Field>

        {/* Action bar */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 22, paddingTop: 16, borderTop: "1px solid var(--color-border)" }}>
          <button onClick={handleReset} style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-border)", background: "transparent", color: "var(--color-text-secondary)", cursor: "pointer", fontSize: 12, fontWeight: 600 }}>{t.reset}</button>
          <button onClick={handleSave} disabled={!dirty} style={{ padding: "9px 22px", borderRadius: 6, border: "none", background: dirty ? "var(--color-accent)" : "var(--color-border)", color: dirty ? "var(--color-accent-text)" : "var(--color-text-muted)", cursor: dirty ? "pointer" : "not-allowed", fontSize: 13, fontWeight: 700 }}>{t.save}</button>
        </div>
      </div>

      {toast && <Toast message={toast} onClose={() => setToast(null)} />}
    </div>
  );
}
